import jsSHA from 'jssha';
import Cookies from 'js-cookie';

export default class SmsEcap {
  constructor(context) {
    this.elements = {
      overlay: context,
      html: document.documentElement,
    };
    this.submittedEmail = null;
    this.submittedPhone = null;
    this.init();
  }

  init() {
    this.ctaBtnEvtListeners();
  }

  ctaBtnEvtListeners() {
    const nextButtons = this.elements.overlay.querySelectorAll('.cta_positive');

    // Loop through all "Next" buttons and add a click event listener
    nextButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        // Find the closest parent step element
        const currentStep = button.closest('.steps--item');

        // Find the input within the current step
        const inputEl = currentStep.querySelector('.js-bfcm-input');

        // Only validate if an input exists on the current step
        if (inputEl) {
          this.validateInput(currentStep);
        } else {
          // If no input (like on the confirmation step), just go to the next step
          this.goToNextStep(currentStep);
        }
      });
    });
  }

  validateInput(currentEl) {
    const inputType = currentEl.querySelector('.js-bfcm-input').type;
    const inputEl = currentEl.querySelector(
      `.js-bfcm-input[type="${inputType}"]`
    );

    // Basic validation regex patterns
    const validationRegex = {
      tel: /^1? ?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    };

    const passedValidation = () =>
      validationRegex[inputType].test(inputEl.value);

    if (passedValidation()) {
      const validValue =
        inputType === 'tel'
          ? this.formatPhoneE164(inputEl.value)
          : inputEl.value;

      // After validation, you will call the appropriate submission method.
      // We will then call goToNextStep inside those methods once the submission is successful.
      currentEl.querySelector('form').classList.add('email-capture--loading');
      if (inputType === 'tel') {
        this.submittedPhone = validValue;
        this.phoneSubmit(validValue, currentEl);
      } else {
        this.submittedEmail = validValue;
        this.emailSubmit(validValue, currentEl);
      }
    } else {
      this.outputError(inputType, currentEl);
    }
  }

  /**
   * Formats a raw phone number input into E.164 format (e.g. +11234567890),
   * assuming a US/Canada number when no country code is present.
   */
  formatPhoneE164(rawValue) {
    const digits = rawValue.replace(/\D/g, '');
    const tenDigits = digits.length === 11 ? digits.slice(1) : digits;
    return `+1${tenDigits}`;
  }

  outputError(inputType, currentEl) {
    // Clear any existing error states
    const allInputs = currentEl.querySelectorAll('.js-bfcm-input');
    allInputs.forEach((input) => input.classList.remove('error'));
    const allErrorLabels = currentEl.querySelectorAll('.email-input--message');
    allErrorLabels.forEach((label) => (label.ariaHidden = true));
    // depending on input type, add the appropriate text to error label
    let errorText = '';
    if (inputType == 'tel') {
      errorText = 'Enter a valid phone number';
    } else if (inputType == 'emailExists') {
      errorText = 'Oops! That email address is already in use.';
    } else {
      errorText = 'Enter a valid email address.';
    }
    const formEl = currentEl.querySelector('form'),
      inputEl = currentEl?.querySelector(`.js-bfcm-input[type="${inputType}"]`),
      errorLabel = formEl.querySelector('.input-message');
    inputEl?.classList.add('error');
    errorLabel.innerHTML = errorText;
    errorLabel.ariaHidden = false;
  }

  goToNextStep(currentStep) {
    const nextStep = currentStep.nextElementSibling;
    // Check if there is a next step
    if (nextStep && nextStep.classList.contains('steps--item')) {
      // Hide the current step
      currentStep.classList.remove('active');
      currentStep.style.display = 'none';

      // Show the next step
      nextStep.classList.add('active');
      nextStep.style.display = 'flex';

      // You can add more logic here, like focusing on the next input
      this.setTabNavBehavior(nextStep);
    }
  }

  phoneSubmit(phone, currentEl) {
    const keyword =
      currentEl.querySelector('.js-bfcm-input').getAttribute('data-keyword') ||
      'TXTBFCM'; //get keyword from theme settings. default to TXTBFCM if left blank
    const cookieName =
      currentEl.querySelector('.js-bfcm-input').getAttribute('data-cookie') ||
      window?.Brooklinen?.themeSettings?.gatedCookie?.name;
    const form = currentEl.querySelector('form');

    const payload = { phone: phone, sourceId: '1365204' };
    if (this.submittedEmail) payload.email = this.submittedEmail;

    fetch('https://5w2edfby7e.execute-api.us-west-2.amazonaws.com/subscribe', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'content-type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(() => {
        try {
          window.heap.track('SMS Optin', {
            phone: phone,
            signup_source: keyword,
          });
        } catch (e) {
          throw new Error(`Heap tracking failed: ${e}`);
        }

        if (cookieName !== '') {
          const expiration = window?.Brooklinen?.themeSettings?.gatedCookie
            ?.expiration
            ? new Date(
                window?.Brooklinen?.themeSettings?.gatedCookie?.expiration
              )
            : null;
          Cookies.set(cookieName, true, {
            expires: expiration,
          });
        }

        form.classList.remove('email-capture--loading');
        this.goToNextStep(currentEl);
      })
      .catch((error) => {
        form.classList.remove('email-capture--loading');
        throw new Error(`Subscribe API error: ${error}`);
      });
  }

  emailSubmit(email, currentEl) {
    const form = currentEl.querySelector('form');
    const signupSrc = 'EMBFCM';

    const payload = { email: email, sourceId: '1365204' };
    if (this.submittedPhone) payload.phone = this.submittedPhone;

    fetch('https://5w2edfby7e.execute-api.us-west-2.amazonaws.com/subscribe', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'content-type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        const shaObj = new jsSHA('SHA-256', 'TEXT');
        shaObj.update(email.toLowerCase());
        const emailHash = shaObj.getHash('HEX');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          submittedEmail: emailHash,
        });

        //Used for Ometria identify in GTM
        window.dataLayer.push({
          event: 'dl_subscribe',
          formLocation: 'bfcm_ecap',
        });
        const emailSubscription = data?.subscriptionResponses?.find(
          (subscriptionResponse) =>
            subscriptionResponse.subscription?.channel === 'EMAIL'
        );
        const emailAlreadyExists =
          emailSubscription?.subscriptionCreationStatus ===
          'PREVIOUSLY_CREATED';

        if (!emailAlreadyExists || !window?.Brooklinen?.ometriaErrorHandling) {
          try {
            window.heap.track('Email Optin', {
              email: email,
              signup_source: signupSrc,
            });
          } catch (e) {
            throw new Error(`Heap tracking failed: ${e}`);
          }
          //formWrapper?.querySelector('.email-input')?.classList?.remove('error')

          this.goToNextStep(currentEl);
        } else {
          this.outputError(
            'emailExists',
            document.querySelector('#bfcm .steps-wrapper')
          );
        }
        form.classList.remove('email-capture--loading');
        return data;
      })
      .catch((error) => {
        form.classList.remove('email-capture--loading');
        throw new Error(`Subscribe API error: ${error}`);
      });
  }
}
